import { AuthenticatedTemplate } from "@azure/msal-react";
import { Grid, Button } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/core/styles";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";

import { AddTopicPayload, DeleteTopicPayload } from "./model/topicsPayload";
import { config } from "utilities/config";
import { getMentoringTopicsListSchema } from "./model/mentoringTopicsSchema";
import { getTopicsListUiSchema } from "./model/topicsUiSchema";
import { globalTheme } from "style/globalTheme";
import { MentorshipAvailabilityPayload } from "app/services/payloadSubmit/mentorshipAvailabilityPayload";
import { mentorshipAvailabilitySchema } from "app/components/mvp/formWidget/model/mentorshipAvailabilityJson";
import { mentorshipAvailabilityUiSchema } from "app/components/uiSchema/mentorshipAvailabilityUiSchema";
import { post } from "app/services/api";
import { useStyles } from "./style";
import FormWidget from "app/components/mvp/formWidget";
import ListWidget from "app/components/mvp/listWidget";
import TabWidget from "app/components/molecules/tab/TabWidget.mui";

function MentorshipManageAvailability(props) {
  // Initialisation
  const [availabilityFormData, setAvailabilityFormData] = useState({});
  const [selectedTab, setSelectedTab] = useState(1);
  const contactid = useSelector((state) => state.contactid);
  const initialFormDataRef = useRef(null);
  // Use ref to store current form data without causing re-renders
  const currentFormDataRef = useRef({});

  // Memoize availability object to prevent unnecessary re-renders
  // Only use state for initial form data - FormWidget manages its own state during typing
  const availability = useMemo(() => ({
    widgetType: "updateContact",
    formSchema: mentorshipAvailabilitySchema,
    uiSchema: mentorshipAvailabilityUiSchema,
    formData: availabilityFormData, // Only used for initial load
    initialFormData: initialFormDataRef.current || availabilityFormData,
    apiURL: config.teleportal.submitForm.Update,
    payloadSchema: {},
    entityType: "contacts",
    formUischema: "",
    button1Label: "Save",
    button2Label: "Go Back",
    payloadSubmit: MentorshipAvailabilityPayload,
    contactId: contactid,
    goBackPage: "/",
    button2GoBack: true,
    historyUrl: "",
  }), [availabilityFormData, contactid]);

    // Columns for Topics tab
    const topicsColumns = [
      {
        field: "topicName",
        flex: 1,
        headerName: "Topic",
        headerClassName: "super-app-theme--header",
      },
    ];

  // State
  const [topicsData, setTopicsData] = useState([]);

  // useEffect
  useEffect(() => {
    if (contactid.length > 0) {
      getAvailabilityDetails();
      // Topics data will be loaded when Topics tab is clicked
    }
  }, [contactid]);

    // Hooks other than useEffect (useRef, useCallback)
    const classes = useStyles();
    const topicsDataLoadedRef = useRef(false);
    const topicsLoadingRef = useRef(false);
    const dispatch = useDispatch();

  // Function Handlers
  const getTopicsData = () => {
    if (!contactid || contactid.length === 0) {
      return;
    }

    // Prevent multiple simultaneous API calls
    if (topicsLoadingRef.current) {
      return;
    }

    topicsLoadingRef.current = true;
    const api = config.teleportal.mentoring.listTopicsFromContacts;
    const payload = {
      contactid: contactid,
    };

    // Use direct axios call to access full error response structure
    // This allows us to check for "No Topics found" message in error.response.data
    axios.defaults.headers.common["Authorization"] =
      "Bearer " + localStorage.getItem("token");
    
    axios
      .post(`${config.teleportal.env}${api}`, payload)
      .then((res) => {
        if (Array.isArray(res.data)) {
          // Add unique id to each row for DataGrid and ensure ctpt_topicsid is preserved
          const dataWithIds = res.data.map((item, index) => {
            // Try to find the actual topic ID from various possible field names
            // The FetchXML uses alias "ac" for the linked topic entity
            const topicId = item.ctpt_topicsid || 
                           item["ac.ctpt_topicsid"] ||
                           item["ac_ctpt_topicsid"] ||
                           item._ctpt_topicsid_value ||
                           item["ctpt_topicsid@OData.Community.Display.V1.FormattedValue"] ||
                           // Check all keys that might contain the topic ID
                           (() => {
                             const keys = Object.keys(item);
                             const idKey = keys.find(key => 
                               key.toLowerCase().includes('topicsid') || 
                               (key.toLowerCase().includes('topicid') && !key.toLowerCase().includes('contact'))
                             );
                             return idKey ? item[idKey] : null;
                           })() ||
                           item.id ||
                           null;
            
            // Use topicId if it's a GUID, otherwise generate a unique ID
            const rowId = topicId && topicId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) 
                          ? topicId 
                          : (item.id || `topic-${index}`);
            
            return {
              ...item,
              id: rowId,
              // Ensure ctpt_topicsid is available for deletion - preserve the actual GUID if found
              ctpt_topicsid: topicId || item.ctpt_topicsid || item._ctpt_topicsid_value,
            };
          });
          setTopicsData(dataWithIds);
          topicsDataLoadedRef.current = true;
        } else {
          setTopicsData([]);
          topicsDataLoadedRef.current = true;
        }
        topicsLoadingRef.current = false;
      })
      .catch((error) => {
        topicsLoadingRef.current = false;
        
        // Check if the error is "No Topics found" - treat it as empty state, not an error
        // Access the error message from response.data where BadRequestErrorMessageResult puts it
        const errorMessage = error?.response?.data?.Message || 
                            error?.response?.data?.message || 
                            error?.response?.data?.error ||
                            error?.message || 
                            '';
        
        // Check if this is the "No Topics found" case (case-insensitive)
        const isNoTopicsFound = errorMessage && 
                               errorMessage.toLowerCase().includes('no topics found');
        
        if (isNoTopicsFound) {
          // No topics is a valid state, not an error - just set empty array
          // The table will show "No topics found" via the empty state overlay
          setTopicsData([]);
          topicsDataLoadedRef.current = true;
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "No Topics found",
              code: "",
              display: "show",
              severity: "error"
            }
          })
        } else {
          // Only show error for actual errors, not for "no topics found"
          setTopicsData([]);
          topicsDataLoadedRef.current = true;
          dispatch({
            type: "SET_STATUS",
            payload: {
              message: "Failed to load topics",
              code: "",
              display: "show",
              severity: "error",
            },
          });
        }
      });
  };

  // Memoize updateFormData to prevent unnecessary re-renders
  // Store form data in ref instead of state to prevent flickering during typing
  // FormWidget manages its own internal state, so we don't need to sync on every keystroke
  const updateFormData = useCallback((entityType, formData) => {
    // Store in ref to avoid triggering re-renders during typing
    currentFormDataRef.current = formData;
    // Don't update state here - it causes the availability object to be recreated
    // which triggers FormWidget re-renders and causes flickering
  }, []);

  const getAvailabilityDetails = () => {
    let api;
    let payloads;
    const formData = {};
    api = config.teleportal.submitForm.getId;

    payloads = {
      properties: [
        "contactid",
        "ctpt_optedintosaiceconnect",
        "ctpt_availabledaytime",
        "ctpt_availableevenings",
        "ctpt_availableonweekdays",
        "ctpt_availableonweekends",
        "ctpt_availabilitynotes",
        "ctpt_connectmentee"
      ],
      formattedFields: [],
      recordType: "contacts",
      id: contactid,
    };

    post(api, payloads)
      .then((res) => {
        res.data.data.forEach((x) => {
          formData[x.name] = x.value;
        });
        // Store initial form data in ref to keep it stable
        initialFormDataRef.current = { ...formData };
        currentFormDataRef.current = { ...formData };
        // Update state only on initial load - this won't cause flicker
        setAvailabilityFormData(formData);
      })
      .catch((error) => {
        console.error("Error fetching availability details:", error);
        dispatch({
          type: "SET_STATUS",
          payload: {
            message: "Failed to load availability details",
            code: "",
            display: "show",
            severity: "error",
          },
        });
      });
  };

  const getResponse = (res) => {
    if (res.data.id) {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Mentorship availability updated successfully",
          code: "",
          display: "show",
          severity: "success",
        },
      });
      // Refresh availability data after successful update
      if (contactid.length > 0) {
        getAvailabilityDetails();
      }
    } else {
      dispatch({
        type: "SET_STATUS",
        payload: {
          message: "Failed to update mentorship availability",
          code: "",
          display: "show",
          severity: "error",
        },
      });
    }
  };

  // Others
  const TopicsTabContent = () => {
    useEffect(() => {
      // Load topics data when this component mounts (i.e., when Topics tab is clicked)
      // Only load if we haven't loaded yet and contactid is available
      if (contactid && contactid.length > 0 && !topicsDataLoadedRef.current && !topicsLoadingRef.current) {
        getTopicsData();
      }
    }, []); // Empty dependency array - only run on mount

    // Configuration for Topics Grid
    const topicsConfiguration = {
      apiURL: config.teleportal.mentoring.listTopicsFromContacts,
      apiUrlDel: config.teleportal.submitForm.Update,
      columns: topicsColumns,
      searchPayload: "searchFilter",
      getlistPayload: {},
      deleteListPayload: DeleteTopicPayload,
      formwidgetOptions: {
        widgetType: "update",
        formSchema: getMentoringTopicsListSchema,
        uiSchema: getTopicsListUiSchema,
        apiURL: config.teleportal.submitForm.Create,
        entityType: "ctpt_topics",
        formUischema: "",
        button1Label: "Add",
        button2Label: "Cancel",
        button3Label: "Clear",
        payloadSubmit: AddTopicPayload,
        columns: topicsColumns,
        contactId: contactid,
        historyUrl: "",
      },
      listHeading: "",
      nonRecord: false,
      role: "",
      rows: "",
      localDataStore: false,
      hideControls: false,
      hideAdd: false,
      hasAggregate: false,
      staticTable: true,
      staticRow: topicsData,
      formatStaticRow: false,
      hiddenColumn: {},
      contactId: contactid,
      filteredValue: "",
      hasFilter: false,
      filterField: "",
      filterValue: [],
      aggregateColumns: [],
      aggregateField: "",
      hasPayloadfilter: false,
      hideDelete: false,
      hideSearch: true,
      hideFooterPagination: true,
      enableLocalSearch: true,
      pageSize: 50,
      hideModalOnSubmit: true,
      getRowId: (row) => row.id,
      onRecordEdit: getTopicsData,
      emptyStateMessage: "No topics found",
    };

    return (
      <Grid container spacing={0} justify="center">
        <Grid item xs={12} md={10} className={classes.gridContainer}>
          <ListWidget options={topicsConfiguration} />
        </Grid>
      </Grid>
    );
  };

  return (
    <ThemeProvider theme={globalTheme}>
      <AuthenticatedTemplate>
        <div className={classes.container}>
          <TabWidget
            value={selectedTab}
            onChange={setSelectedTab}
            options={{
              defaultTabID: 1,
              tabNavJson: [
                {
                  label: "Manage Availability",
                  id: 1,
                  entity: (
                    <Grid container spacing={0} justify="center">
                      <Grid item xs={12} md={9}>
                        <FormWidget
                          options={availability}
                          updateFormData={updateFormData}
                          successResponse={getResponse}
                        />
                      </Grid>
                    </Grid>
                  ),
                },
                {
                  label: "Topics",
                  id: 2,
                  entity: <TopicsTabContent />,
                },
              ],
            }}
          />
        </div>
        {selectedTab === 2 && (
          <div style={{ 
            width: "100%", 
            marginTop: "-24px",
            paddingLeft: "2rem", 
            paddingRight: "2rem",
            position: "relative",
            zIndex: 10
          }}>
            <Grid container spacing={0} justify="center">
              <Grid item xs={12} md={10} style={{ paddingLeft: "16px" }}>
                <Button
                  variant="text"
                  onClick={() => setSelectedTab(1)}
                  className={classes.goBackButton}
                >
                  Go Back
                </Button>
              </Grid>
            </Grid>
          </div>
        )}
      </AuthenticatedTemplate>
    </ThemeProvider>
  );
}

export default MentorshipManageAvailability;
